import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  Avatar,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import {
  Person,
  SwapHoriz,
  Warning,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { hasPermission } from '../../utils/permissions';

const LeadReassignDialog = ({
  open,
  onClose,
  onReassign,
  lead,
  assignableUsers = [],
  loading = false,
}) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const { user } = useSelector(state => state.auth);

  useEffect(() => {
    if (open) {
      setSelectedUser('');
      setReason('');
      setError('');
    }
  }, [open]);

  const handleReassign = async () => {
    if (!selectedUser) {
      setError('Please select a user to reassign to');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for reassignment');
      return;
    }

    try {
      await onReassign(lead.id, {
        assigned_to: selectedUser,
        reason: reason.trim()
      });
      onClose();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reassign lead');
    }
  };

  const canReassign = () => {
    if (!user || !lead) return false;

    // Check role-based permissions
    if (hasPermission(user.role, 'leads.assign')) {
      // Admins can reassign any lead
      if (user.role === 'admin') return true;

      // Managers can reassign leads from their assigned states
      if (user.role === 'manager') {
        return user.assigned_states?.includes(lead.state);
      }

      // Team leads can reassign leads from their assigned states + diseases
      if (user.role === 'team_lead') {
        const hasStateAccess = user.assigned_states?.includes(lead.state);
        const hasDiseaseAccess = user.assigned_diseases?.includes(lead.disease);
        return hasStateAccess || hasDiseaseAccess;
      }
    }

    return false;
  };

  const getAvailableUsers = () => {
    return assignableUsers.filter(u => u.id !== lead?.assigned_to);
  };

  const getCurrentAssignee = () => {
    return assignableUsers.find(u => u.id === lead?.assigned_to);
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'error',
      manager: 'warning',
      team_lead: 'info',
      bda: 'success'
    };
    return colors[role] || 'default';
  };

  if (!canReassign()) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Reassign Lead</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            You do not have permission to reassign this lead.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const availableUsers = getAvailableUsers();
  const currentAssignee = getCurrentAssignee();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHoriz />
          Reassign Lead
        </Box>
        {lead && (
          <Typography variant="caption" display="block" color="text.secondary">
            Lead ID: {lead.id} • {lead.patient_name}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Current Assignment */}
        {currentAssignee && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Currently Assigned To:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 32, height: 32 }}>
                <Person />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {currentAssignee.first_name} {currentAssignee.last_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={currentAssignee.role}
                    size="small"
                    color={getRoleColor(currentAssignee.role)}
                  />
                  {currentAssignee.username && (
                    <Typography variant="caption" color="text.secondary">
                      @{currentAssignee.username}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* New Assignment */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Reassign To *</InputLabel>
          <Select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            label="Reassign To *"
            disabled={loading}
          >
            {availableUsers.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Avatar sx={{ width: 24, height: 24 }}>
                    <Person fontSize="small" />
                  </Avatar>
                  <ListItemText
                    primary={`${user.first_name} ${user.last_name}`}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={user.role}
                          size="small"
                          color={getRoleColor(user.role)}
                        />
                        {user.username && (
                          <span>@{user.username}</span>
                        )}
                      </Box>
                    }
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {availableUsers.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No eligible users available for reassignment.
          </Alert>
        )}

        <TextField
          fullWidth
          label="Reason for Reassignment *"
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={loading}
          placeholder="Provide a clear reason for this reassignment..."
          helperText="This will be recorded in the lead's activity history"
        />

        {/* Permission Info */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="caption" display="block">
            <strong>Your Reassignment Permissions:</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.role === 'admin' && 'You can reassign any lead as an admin.'}
            {user.role === 'manager' && `You can reassign leads from your assigned states: ${user.assigned_states?.join(', ') || 'None'}`}
            {user.role === 'team_lead' && `You can reassign leads from your assigned states (${user.assigned_states?.join(', ') || 'None'}) and diseases (${user.assigned_diseases?.join(', ') || 'None'})`}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleReassign}
          disabled={loading || !selectedUser || !reason.trim() || availableUsers.length === 0}
          startIcon={<SwapHoriz />}
        >
          {loading ? 'Reassigning...' : 'Reassign Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadReassignDialog;